import type { AppContext, ViewCleanup } from "../app";
import { loadCountries } from "../data/countries";
import { loadState } from "../state/storage";

// Mastery heatmap: color every country by its mastery level (0–5). Reveals
// where the user is strong vs weak at a glance, and which regions still
// need attention. Read-only — no interaction beyond exploring the globe.

// Per-level fills, chosen for clear progression on a dark background and
// distinguishability for common red-green color blindness (the gradient
// has luminance + hue components, not just hue).
const LEVEL_FILLS: Record<number, string> = {
  0: "#3b4252", // unseen — same as default LAND
  1: "#7c4856", // attempted / weak
  2: "#a56b46",
  3: "#c8a13b",
  4: "#88a83b",
  5: "#56c08d", // mastered
};

const LEGEND: Array<[number, string]> = [
  [0, "Unseen"],
  [1, "Just learning"],
  [2, "Shaky"],
  [3, "Familiar"],
  [4, "Strong"],
  [5, "Mastered"],
];

export const mountHeatmap = async (
  root: HTMLElement,
  ctx: AppContext,
): Promise<ViewCleanup> => {
  root.classList.add("view-heatmap");
  ctx.globe.setTargetIso(null);
  ctx.globe.setGuessIso(null);
  ctx.globe.setHoverEnabled(true);

  const table = await loadCountries();
  const state = loadState();

  // Build the iso → color map. Countries we have no stats for default to
  // level 0. We deliberately don't shade non-existent ISOs so dependencies
  // / territories keep the neutral LAND fill.
  const heatmap = new Map<string, string>();
  for (const iso of Object.keys(table)) {
    const level = state.stats[iso]?.level ?? 0;
    heatmap.set(iso, LEVEL_FILLS[level] ?? LEVEL_FILLS[0]!);
  }
  ctx.globe.setHeatmap(heatmap);

  // Tally per-level counts for the legend
  const counts: number[] = [0, 0, 0, 0, 0, 0];
  for (const iso of Object.keys(table)) {
    const level = state.stats[iso]?.level ?? 0;
    const slot = Math.max(0, Math.min(5, level));
    counts[slot] = (counts[slot] ?? 0) + 1;
  }

  const card = document.createElement("section");
  card.className = "heatmap-card";
  card.innerHTML = `
    <header class="heatmap-header">
      <button type="button" class="heatmap-back" aria-label="Back">← Back</button>
      <h1>Mastery</h1>
    </header>
    <ul class="heatmap-legend" role="list">
      ${LEGEND.map(
        ([level, label]) => `
          <li>
            <span class="heatmap-swatch" style="background:${LEVEL_FILLS[level]}"></span>
            <span class="heatmap-label">${label}</span>
            <span class="heatmap-count">${counts[level]}</span>
          </li>`,
      ).join("")}
    </ul>
  `;
  root.appendChild(card);

  const back = card.querySelector(".heatmap-back") as HTMLButtonElement;
  const onBack = (): void => {
    ctx.globe.setHeatmap(null);
    ctx.nav.goRegionPicker();
  };
  back.addEventListener("click", onBack);

  return () => {
    ctx.globe.setHeatmap(null);
    back.removeEventListener("click", onBack);
    root.classList.remove("view-heatmap");
  };
};
